import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowDownRight, ArrowRight, ArrowUpRight, Clock, Download, Flag, LineChart, Loader2, MoreHorizontal, Pencil, Plus, Printer, Sparkles, Trash2, Repeat, Save,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AbaChart, ChartLegend } from "@/components/AbaChart";
import { SessionsTable } from "@/components/SessionsTable";
import { SessionDialog } from "@/components/SessionDialog";
import { StatusDialog } from "@/components/StatusDialog";
import { ActionDialog } from "@/components/ActionDialog";
import { StatusBadge } from "@/components/ActionsSidebar";
import { api, errorMessage, API_BASE, TOKEN_KEY } from "@/lib/api";
import { fmtDate, pct, trendLabel, deadlineLabel, deadlineClass } from "@/lib/aba";
import { ACTIONS, SESSIONS, CHART } from "@/constants/testIds";

export const ActionDetail = ({ actionId, patientId, onDeleted }) => {
  const qc = useQueryClient();
  const [mode, setMode] = useState("percent");
  const [sessionDialog, setSessionDialog] = useState({ open: false, session: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, stato: null });
  const [editOpen, setEditOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteSession, setDeleteSession] = useState(null);
  const [description, setDescription] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["action", actionId],
    queryFn: async () => (await api.get(`/actions/${actionId}`)).data,
    enabled: Boolean(actionId),
  });
  const action = data?.action;
  const analytics = data?.analytics;

  useEffect(() => {
    setDescription(action?.descrizione || "");
  }, [action?.action_id, action?.descrizione]);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["action", actionId] });
    qc.invalidateQueries({ queryKey: ["actions", patientId] });
    qc.invalidateQueries({ queryKey: ["summary", patientId] });
    qc.invalidateQueries({ queryKey: ["report", patientId] });
    qc.invalidateQueries({ queryKey: ["patients"] });
    qc.invalidateQueries({ queryKey: ["reminders"] });
  };

  const saveDescription = useMutation({
    mutationFn: async () => (await api.put(`/actions/${actionId}`, { descrizione: description })).data,
    onSuccess: () => { invalidateAll(); toast.success("Descrizione salvata"); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const removeAction = useMutation({
    mutationFn: async () => (await api.delete(`/actions/${actionId}`)).data,
    onSuccess: () => { invalidateAll(); toast.success("Azione eliminata"); setDeleteAction(false); onDeleted?.(); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const removeSession = useMutation({
    mutationFn: async (sid) => (await api.delete(`/sessions/${sid}`)).data,
    onSuccess: () => { invalidateAll(); toast.success("Sessione eliminata"); setDeleteSession(null); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_BASE}/actions/${actionId}/export.csv`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("Export non riuscito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aba_${(action?.nome || "azione").replace(/[^a-z0-9]+/gi, "_")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV esportato");
    } catch (e) {
      toast.error(e.message || "Export non riuscito");
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 rounded-xl" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }
  if (isError || !action) {
    return <Card><CardContent className="py-12 text-center text-destructive">Impossibile caricare l'azione.</CardContent></Card>;
  }

  const descDirty = description !== (action.descrizione || "");
  const nextNumber = (analytics?.count || 0) + 1;
  const hasPhase = Boolean(analytics?.cambio_fase_numero);
  const dir = analytics?.trend?.direzione;
  const deadline = deadlineLabel(action);
  const TrendIcon = dir === "crescente" ? ArrowUpRight : dir === "decrescente" ? ArrowDownRight : ArrowRight;

  return (
    <motion.div key={actionId} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }} className="space-y-4" data-testid="action-detail">
      {/* Header */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading text-2xl font-semibold tracking-tight" data-testid="action-title">{action.nome}</h2>
                <StatusBadge stato={action.stato} />
                {analytics?.stale && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-2 py-0.5" data-testid="action-stale-badge">
                    <Clock className="h-3 w-3" /> Nessun dato da {analytics.giorni_inattivita} giorni
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                <span>Introdotto il <span className="text-foreground font-medium">{fmtDate(action.data_introduzione)}</span></span>
                {action.stato === "acquisito" && <span>Acquisito il <span className="text-foreground font-medium">{fmtDate(action.data_acquisizione)}</span></span>}
                <span>Criterio <span className="text-foreground font-medium">{action.criterio}%</span> x 3 sessioni</span>
                {action.data_obiettivo && <span>Obiettivo entro <span className="text-foreground font-medium">{fmtDate(action.data_obiettivo)}</span></span>}
              </p>
              {deadline && (
                <span className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium border rounded-md px-2 py-1 ${deadlineClass(deadline.tone)}`} data-testid="action-deadline-badge" data-tone={deadline.tone}>
                  <Flag className="h-3 w-3" /> {deadline.text}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 no-print">
              <Button className="gap-2 h-10" onClick={() => setSessionDialog({ open: true, session: null })} data-testid={SESSIONS.addButton}>
                <Plus className="h-4 w-4" /> Aggiungi sessione
              </Button>
              <Button variant="outline" className="gap-2 h-10" onClick={() => setStatusDialog({ open: true, stato: null })} data-testid={ACTIONS.statusButton}>
                <Repeat className="h-4 w-4" /> {action.stato === "acquisito" ? "Riporta a introdotto" : "Segna acquisito"}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-10 w-10" aria-label="Altre azioni" data-testid="action-menu-button"><MoreHorizontal className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setEditOpen(true)} className="gap-2" data-testid="action-edit-button"><Pencil className="h-4 w-4" /> Modifica nome / criterio</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportCsv} disabled={exporting} className="gap-2" data-testid={ACTIONS.exportButton}><Download className="h-4 w-4" /> Esporta CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.print()} className="gap-2" data-testid={ACTIONS.printButton}><Printer className="h-4 w-4" /> Stampa grafico</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setDeleteAction(true)} className="gap-2 text-destructive focus:text-destructive" data-testid={ACTIONS.deleteButton}><Trash2 className="h-4 w-4" /> Elimina azione</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Description box */}
          <div className="mt-5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Azione che il bambino deve compiere</label>
            <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrivi l’azione richiesta (es. “Il bambino batte le mani su richiesta verbale ‘Batti le mani’ entro 3 secondi, senza prompt”)."
                className="bg-card min-h-[72px] flex-1"
                data-testid={ACTIONS.descriptionTextarea}
              />
              {descDirty && (
                <Button variant="secondary" className="gap-2 sm:self-end" onClick={() => saveDescription.mutate()} disabled={saveDescription.isPending} data-testid={ACTIONS.descriptionSave}>
                  {saveDescription.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suggestion banner */}
      {analytics?.suggerisci_acquisito && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-amber-200 bg-amber-50 text-amber-950 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={ACTIONS.suggestionBanner}>
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Criterio di padronanza raggiunto</p>
              <p className="text-sm text-amber-900/80">≥{action.criterio}% per {analytics.streak_criterio} sessioni consecutive. Suggerito: segnare l'azione come <strong>Acquisita</strong>.</p>
            </div>
          </div>
          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white shrink-0" onClick={() => setStatusDialog({ open: true, stato: "acquisito" })} data-testid={ACTIONS.suggestionAccept}>Segna come acquisito</Button>
        </motion.div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi label="Sessioni" value={analytics.count} sub={`${analytics.sessioni_introdotto} intro · ${analytics.sessioni_acquisito} acq.`} testId="kpi-sessions" />
        <Kpi label="Ultima" value={pct(analytics.ultima)} sub={analytics.points.length ? fmtDate(analytics.points[analytics.points.length - 1].data) : "—"} />
        <Kpi label="Media" value={pct(analytics.media)} sub={`Livello recente ${pct(analytics.livello_recente)}`} />
        <Kpi label="Trend" value={<span className="flex items-center gap-1"><TrendIcon className={`h-5 w-5 ${dir === "crescente" ? "text-emerald-600" : dir === "decrescente" ? "text-rose-600" : "text-muted-foreground"}`} /> {trendLabel(dir)}</span>} sub={analytics.trend.slope !== null ? `${analytics.trend.slope > 0 ? "+" : ""}${analytics.trend.slope}% / sessione` : "Servono 2+ sessioni"} />
        <Kpi label="Streak criterio" value={`${analytics.streak_criterio}/3`} sub={analytics.criterio_raggiunto ? "Criterio raggiunto" : `Sessioni consecutive ≥${action.criterio}%`} />
        <Kpi label="Indipendenza" value={pct(analytics.percent_indipendente)} sub={`${analytics.prompt_counts?.indipendente || 0} sess. senza aiuto`} testId="kpi-independence" />
      </div>

      {/* Chart card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="font-heading text-base flex items-center gap-2"><LineChart className="h-4 w-4 text-primary" /> Grafico ABA — {action.nome}</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{mode === "cumulative" ? "Registro cumulativo: somma progressiva delle risposte corrette; la pendenza indica il tasso di apprendimento." : mode === "count" ? "Conteggio grezzo: risposte corrette rispetto al totale delle prove di ogni sessione." : "Grafico a intervalli uguali: percentuale di risposte corrette per sessione, indipendente dalle altre azioni."}</p>
            </div>
            <div className="flex items-center gap-3 no-print">
              <div className="min-w-[150px]" data-testid={ACTIONS.progress}>
                <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                  <span>Sessioni raccolte</span>
                  <span className="font-medium text-foreground tabular">{Math.min(analytics.count, analytics.target)}/{analytics.target}{analytics.count > analytics.target ? ` (+${analytics.count - analytics.target})` : ""}</span>
                </div>
                <Progress value={Math.min(100, (analytics.count / analytics.target) * 100)} className="h-1.5" />
              </div>
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="h-9">
                  <TabsTrigger value="percent" className="text-xs" data-testid={CHART.modePercent}>Percentuale</TabsTrigger>
                  <TabsTrigger value="count" className="text-xs" data-testid={CHART.modeCount}>Conteggio</TabsTrigger>
                  <TabsTrigger value="cumulative" className="text-xs" data-testid="chart-mode-cumulative">Cumulativo</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {analytics.count === 0 ? (
            <div className="h-[280px] flex flex-col items-center justify-center text-center gap-3 rounded-lg border border-dashed border-border bg-muted/30" data-testid="chart-empty-state">
              <span className="h-12 w-12 rounded-xl bg-card border border-border flex items-center justify-center"><LineChart className="h-6 w-6 text-primary" /></span>
              <div>
                <p className="font-medium">Nessuna sessione registrata</p>
                <p className="text-sm text-muted-foreground">Il grafico compare dal primo dato e si completa con {analytics.target} sessioni.</p>
              </div>
              <Button variant="secondary" className="gap-2" onClick={() => setSessionDialog({ open: true, session: null })} data-testid="chart-empty-add-session"><Plus className="h-4 w-4" /> Aggiungi prima sessione</Button>
            </div>
          ) : (
            <>
              <AbaChart analytics={analytics} mode={mode} />
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <ChartLegend criterio={action.criterio} hasPhase={hasPhase} mode={mode} promptCounts={analytics.prompt_counts} />
                {!analytics.complete && <p className="text-xs text-muted-foreground">In raccolta: mancano {analytics.target - analytics.count} sessioni per il grafico completo.</p>}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Sessions table */}
      {analytics.count > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading font-semibold">Sessioni registrate <span className="text-muted-foreground font-normal">({analytics.count})</span></h3>
          </div>
          <SessionsTable points={analytics.points} criterio={action.criterio} onEdit={(s) => setSessionDialog({ open: true, session: s })} onDelete={(s) => setDeleteSession(s)} />
        </div>
      )}

      <SessionDialog open={sessionDialog.open} onOpenChange={(o) => setSessionDialog((s) => ({ ...s, open: o }))} action={action} session={sessionDialog.session} nextNumber={nextNumber} />
      <StatusDialog open={statusDialog.open} onOpenChange={(o) => setStatusDialog((s) => ({ ...s, open: o }))} action={action} defaultStato={statusDialog.stato} />
      <ActionDialog open={editOpen} onOpenChange={setEditOpen} patientId={patientId} domain={action.domain} action={action} />

      <AlertDialog open={deleteAction} onOpenChange={setDeleteAction}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare “{action.nome}”?</AlertDialogTitle>
            <AlertDialogDescription>Verranno eliminate anche tutte le {analytics.count} sessioni registrate. L'operazione non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={(e) => { e.preventDefault(); removeAction.mutate(); }} data-testid="action-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deleteSession)} onOpenChange={(o) => !o && setDeleteSession(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la sessione {deleteSession?.numero}?</AlertDialogTitle>
            <AlertDialogDescription>Le sessioni successive verranno rinumerate. L'operazione non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={(e) => { e.preventDefault(); removeSession.mutate(deleteSession.session_id); }} data-testid="session-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

const Kpi = ({ label, value, sub, testId }) => (
  <Card className="shadow-none">
    <CardContent className="p-4">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-heading text-xl font-semibold mt-1 tabular" data-testid={testId}>{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>
    </CardContent>
  </Card>
);
